import { createFileRoute, notFound, redirect } from '@tanstack/react-router';

import BlogPostPage from '@/pages/blog/BlogPostPage';
import { relatedBlogPosts } from '@/pages/blog/utils/related-posts';
import {
  getBlogPost,
  getBlogPosts,
  getSanitySocialImageUrl,
} from '@/sanity/blog';
import { getFooterLandingPages } from '@/sanity/landing-page';
import { getLegalLinks } from '@/sanity/legal-document';
import { getRetiredBlogRedirect } from '@/utils/retired-blog-redirects';
import {
  absoluteUrl,
  buildBreadcrumbSchema,
  buildFaqPageSchema,
  buildOrganizationSchema,
  buildPageMeta,
  buildStructuredDataScript,
  defaultOgImagePath,
  siteName,
  siteUrl,
} from '@/utils/seo';

export const Route = createFileRoute('/blog/$slug')({
  loader: async ({ params }) => {
    const [post, posts, footerLandingPages, footerLegalLinks] =
      await Promise.all([
        getBlogPost({ data: params.slug }),
        getBlogPosts(),
        getFooterLandingPages(),
        getLegalLinks(),
      ]);

    if (post) {
      return {
        footerLandingPages,
        footerLegalLinks,
        post,
        relatedPosts: relatedBlogPosts(post.slug, posts),
      };
    }

    const retiredSlug = getRetiredBlogRedirect(params.slug);
    if (retiredSlug) {
      throw redirect({
        to: '/blog/$slug',
        params: { slug: retiredSlug },
        statusCode: 301,
      });
    }

    throw notFound();
  },
  component: BlogPostPage,
  head: ({ loaderData, params }) => {
    const post = loaderData?.post;
    const title = `${post?.seoTitle ?? post?.title ?? 'Article'} — Budgy`;
    const description =
      post?.seoDescription ?? post?.excerpt ?? 'Landlord advice from Budgy.';
    const path = `/blog/${encodeURIComponent(params.slug)}`;
    const image = post?.coverImage
      ? getSanitySocialImageUrl(post.coverImage)
      : absoluteUrl(defaultOgImagePath);
    const pageMeta = buildPageMeta({
      title,
      description,
      path,
      image,
      imageAlt: post?.coverImage?.alt,
      type: 'article',
    });

    if (!post) {
      return pageMeta;
    }

    const authorIsOrganization = post.author.name
      .trim()
      .toLowerCase()
      .startsWith('budgy');

    return {
      ...pageMeta,
      meta: [
        ...pageMeta.meta,
        {
          property: 'article:published_time',
          content: post.publishedAt,
        },
        {
          property: 'article:modified_time',
          content: post.updatedAt,
        },
        { property: 'article:author', content: post.author.name },
      ],
      scripts: [
        buildStructuredDataScript(
          buildBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title, path },
          ]),
        ),
        buildStructuredDataScript({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description,
          url: absoluteUrl(path),
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': absoluteUrl(path),
          },
          image,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt,
          author: authorIsOrganization
            ? { '@type': 'Organization', name: siteName, url: siteUrl }
            : { '@type': 'Person', name: post.author.name },
          publisher: buildOrganizationSchema(),
        }),
        ...(post.faqs.length > 0
          ? [buildStructuredDataScript(buildFaqPageSchema(post.faqs))]
          : []),
      ],
    };
  },
});
